/**
 * The registration interface for markers — the one thing the module hands back to the app.
 *
 * ```tsx
 * const { isNew, markUsed, spotlight } = useWhatsNewAnchor('tab:lost-business', {
 *     reveal: () => setActiveTab('lostbusiness'),
 * })
 *
 * <Tab onClick={markUsed} className={spotlight ? SPOTLIGHT_CLASS : ''}>
 *     Lost Business {isNew && <NewMarker />}
 * </Tab>
 * ```
 *
 * The id is whatever the app says it is. `tab:`, `button:`, `field:` are conventions of the
 * host, not of the module: nothing here parses an id, and nothing here knows what a tab is.
 * That is D6 in one file.
 *
 * ## `reveal` — how "Show me" opens a place the module has never heard of
 *
 * The panel's "Show me" has to put the reader in front of the thing. Only the app can do that:
 * it might be a tab switch, a scroll, an accordion, a route. So the app registers HOW, the
 * module decides WHEN, and the module still knows nothing. An anchor with no `reveal` simply
 * gets no "Show me" button — better than a button that does nothing.
 *
 * `reveal` is held in a ref and read through a stable wrapper. An inline arrow function as a
 * dependency would re-register on every render, and since registering re-renders the provider,
 * that is an endless loop rather than a slow app. It cost one run to find; the ref is the fix.
 *
 * ## `markUsed` is called on USE, not on sight
 *
 * `onClick`, not `useEffect`. A marker that cleared itself when it scrolled into view would be
 * gone before the reader reached it — which is the failure this whole marker level exists to
 * avoid (D1).
 */

import { useEffect, useRef } from 'react';
import { useWhatsNew } from './WhatsNewProvider';

export interface WhatsNewAnchor {
    /** Show a marker here. False until the state has loaded, and after a failure (D9). */
    isNew: boolean;
    /** Call when the element is actually used. Idempotent, optimistic, survives a reload (D3). */
    markUsed: () => void;
    /** True for a couple of seconds after "Show me" pointed here. Pair with `SPOTLIGHT_CLASS`. */
    spotlight: boolean;
}

export interface WhatsNewAnchorOptions {
    /** How to put this element in front of the reader. Omit it and "Show me" is not offered. */
    reveal?: () => void;
}

export function useWhatsNewAnchor(id: string, options?: WhatsNewAnchorOptions): WhatsNewAnchor {
    const { isAnchorActive, markAnchorUsed, registerAnchor, isSpotlit } = useWhatsNew();

    const reveal = useRef(options?.reveal);
    reveal.current = options?.reveal;

    // The WRAPPER must be absent when there is no `reveal`, not merely call nothing: the panel
    // decides whether to offer "Show me" by asking whether this anchor can be revealed, and a
    // wrapper that is always truthy made it offer a button that did nothing — the opposite of
    // what the paragraph above promises (`/code-review high`, W2).
    const canReveal = !!options?.reveal;
    useEffect(
        () => registerAnchor(id, canReveal ? () => reveal.current?.() : undefined),
        [id, canReveal, registerAnchor],
    );

    return {
        isNew: isAnchorActive(id),
        markUsed: () => markAnchorUsed(id),
        spotlight: isSpotlit(id),
    };
}

/**
 * What "Show me" leaves on the element for a moment.
 *
 * Exported as a constant rather than described in prose so that every app that copies this
 * folder highlights the same way, and so that the ring uses the token (`blue-500` IS the
 * Dolder green — `web/tailwind.config.js`) instead of whatever the next caller reaches for.
 */
export const SPOTLIGHT_CLASS = 'ring-2 ring-blue-500 ring-offset-2 rounded-md';
